/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jul 30 2024
 **/

import { debounce } from "lodash-es";
import { EventEmitter } from "eventemitter3";
import { useAtomCallback } from "jotai/utils";
import { Subscription, timer } from "rxjs";

import { Properties, useProperties } from "@/frameworks/libraries/properties";
import { useEventHandler } from "@rfice/eventhandler";
import { JotaiStore } from "@/frameworks/libraries/commons";

import useEnterScene from "./useEnterScene";
import useUnityCommand from "./useUnityCommand";
import useUnityHandler from "./useUnityHandler";
import { useMyDeskActions } from "../hooks";

const UNITY_SYSTEM_TIMEOUT_PER_SEC = 100000;

const eventemitter = new EventEmitter();

export default function useUnitySystemActions() {
	const unityCommand = useUnityCommand();
	const { go } = useEnterScene();
	const properties = useProperties();
	const myDeskAPI = useMyDeskActions();

	useUnityHandler(
		"useUnitySystemActions",
		{
			// @description 유니티가 마운트 되고 서비스상 준비가 되면 onSpaceStatus 이벤트 호출
			onSpaceStatus: debounce(() => {
				eventemitter.emit("UnityProcessReady", { reason: "UnityProcessReady" });
			}, 1000)
		},
		[]
	);

	useEventHandler(
		"UnityDomUnmount",
		() => {
			eventemitter.emit("UnityProcessDisposed", { reason: "UnityProcessDisposed" });
		},
		[]
	);

	const turnOn = useAtomCallback(
		(get) => {
			unityCommand.activate();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return new Promise<{ response: any }>((resolve) => {
				let timeout: Subscription | null = timer(UNITY_SYSTEM_TIMEOUT_PER_SEC * 1000).subscribe(() => {
					resolve({ response: { reason: "timeout" } });
					timeout = null;
				});

				(async () => {
					const unityLoaded = await get(properties.propertyAtom("unityLoaded"));

					if (unityLoaded) {
						timeout?.unsubscribe();
						timeout = null;
						resolve({ response: { reason: "already loaded" } });
					}
				})();

				eventemitter.on("UnityProcessReady", (payload) => {
					timeout?.unsubscribe();
					timeout = null;
					resolve({ response: payload });
				});
			});
		},
		{
			store: JotaiStore
		}
	);

	const turnOff = useAtomCallback(
		(get) => {
			unityCommand.deactivate();
			return new Promise((resolve) => {
				let timeout: Subscription | null = timer(UNITY_SYSTEM_TIMEOUT_PER_SEC * 1000).subscribe(() => {
					resolve({ response: { reason: "timeout" } });
					timeout = null;
				});

				(async () => {
					const unityLoaded = await get(properties.propertyAtom("unityLoaded"));
					if (!unityLoaded) {
						timeout?.unsubscribe();
						timeout = null;
						resolve({ response: { reason: "already not loaded" } });
					}
				})();

				eventemitter.on("UnityProcessDisposed", (payload) => {
					timeout?.unsubscribe();
					timeout = null;
					resolve({ response: payload });
				});
			});
		},
		{
			store: JotaiStore
		}
	);

	const _setSleep = async () => {
		unityCommand.sleep();
		unityCommand.sleepMode();
		return new Promise((resolve) => {
			resolve({ response: { reason: "sleep state" } });
		});
	};

	const _turnOnAndEnterScene = async (
		arg: SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference,
		options?: Parameters<ReturnType<typeof useEnterScene>["go"]>[1]
	) => {
		return turnOn()
			.then((payload) => {
				console.debug("turnOn()", payload);
				return go(arg, options);
			})
			.catch(() => {
				// _showMyDeskAndTurnOff();
				_showMyDeskAndSleep();
				return Promise.reject();
			});
	};

	/**
	 *
	 * @param arg
	 * @param options
	 * @param type 공간 나가기시, 사용자 제스처로 인한 나가기인지, 시스템 흐름으로 인한 나가기인지 구분
	 * @returns
	 */
	const hideMyDeskAndTurnOnAndEnterScene = async (
		type: "userGuesture" | "system",
		arg: SwaggerModels.UserSpaceResponseVO | SwaggerMeetingAPIModels.Conference,
		options?: Parameters<ReturnType<typeof useEnterScene>["go"]>[1]
	) => {
		if (type === "userGuesture") {
			Properties.property.spaceEnterByUserGuesture = true;
		}
		myDeskAPI.hide();
		return _turnOnAndEnterScene(arg, options);
	};

	/**
	 * ref: https://projects.rsupport.com/issues/216662
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _showMyDeskAndTurnOff = async () => {
		myDeskAPI.show();
		return turnOff();
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _showMyDeskAndSleep = async () => {
		myDeskAPI.show();
		return _setSleep();
	};

	return {
		turnOn,
		turnOff,
		go: hideMyDeskAndTurnOnAndEnterScene,
		// goMyDesk: _showMyDeskAndTurnOff,
		goMyDesk: _showMyDeskAndSleep
	};
}
